import React, { useState, useRef } from 'react';
import { Dropdown, Badge, Spinner } from 'react-bootstrap';
import { FaBell, FaCircle, FaCheckDouble, FaSync, FaTrash } from 'react-icons/fa';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { NotificationDropdownProps, NotificationItem } from './types';
import './NotificationDropdown.scss';

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  notifications,
  unreadCount,
  loading,
  onMarkAsRead,
  onMarkAllAsRead,
  onRefresh,
  onDeleteNotification
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await onRefresh();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const getNotificationIcon = (type: NotificationItem['type']) => {
    switch (type) {
      case 'success':
        return <FaCircle className="notification-icon success" />;
      case 'warning':
        return <FaCircle className="notification-icon warning" />;
      case 'error':
        return <FaCircle className="notification-icon error" />;
      default:
        return <FaCircle className="notification-icon info" />;
    }
  };

  const formatNotificationTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return 'Unknown time';
    }
  };

  const handleNotificationClick = (notification: NotificationItem) => {
    // Mark as read if unread
    if (!notification.isRead) {
      onMarkAsRead(notification.id);
    }

    // Determine navigation route based on notification type or content
    let targetRoute = '/dashboard';

    // Check if it's a support ticket notification
    if (notification.ticketId || notification.relatedTicketId) {
      const ticketId = notification.ticketId || notification.relatedTicketId;
      targetRoute = `/support/ticket/${ticketId}`;
    }
    // Check if message mentions ticket or support
    else if (notification.message?.toLowerCase().includes('ticket') ||
             notification.message?.toLowerCase().includes('support')) {

      // Extract ticket ID from message
      const patterns = [
        /ticket[:\s#]*([a-f0-9]{24})/i,  // ticket: 68cbd1128625ca837ebe0cad
        /ticket[:\s#]*([a-f0-9]{12,})/i, // ticket: shorter IDs
        /id[:\s#]*([a-f0-9]{24})/i,      // id: 68cbd1128625ca837ebe0cad
        /([a-f0-9]{24})/i,               // just the 24-char hex ID
      ];

      let ticketId = null;
      for (const pattern of patterns) {
        const match = notification.message.match(pattern);
        if (match) {
          ticketId = match[1];
          targetRoute = `/support/ticket/${ticketId}`;
          break;
        }
      }

      if (!ticketId) {
        targetRoute = '/support';
      }
    }
    // Check if it's a user-related notification
    else if (notification.userId || notification.relatedUserId) {
      const userId = notification.userId || notification.relatedUserId;
      targetRoute = `/billing-details/${userId}`;
    }
    // Check if it's an order-related notification
    else if (notification.orderId || notification.relatedOrderId) {
      const orderId = notification.orderId || notification.relatedOrderId;
      targetRoute = `/order-details/${orderId}`;
    }
    // Check if message mentions specific admin areas
    else if (notification.message?.toLowerCase().includes('order')) {
      targetRoute = '/orders';
    }
    else if (notification.message?.toLowerCase().includes('user') ||
             notification.message?.toLowerCase().includes('customer')) {
      targetRoute = '/users';
    }
    // Default based on notification type
    else {
      switch (notification.type) {
        case 'success':
        case 'info':
          targetRoute = '/orders';
          break;
        case 'warning':
        case 'error':
          targetRoute = '/support';
          break;
        default:
          targetRoute = '/dashboard';
      }
    }

    // Close the dropdown
    setIsOpen(false);

    // Navigate after a small delay to ensure dropdown closes
    setTimeout(() => {
      navigate(targetRoute);
    }, 100);
  };

  const handleDeleteClick = (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    if (onDeleteNotification) {
      onDeleteNotification(notificationId);
    }
  };

  return (
    <Dropdown
      className="notification-dropdown"
      show={isOpen}
      onToggle={(show) => setIsOpen(show)}
      ref={dropdownRef}
    >
      <Dropdown.Toggle variant="transparent" className="notification-toggle">
        <div className="notification-bell">
          <FaBell size={20} />
          {unreadCount > 0 && (
            <Badge bg="danger" className="notification-badge">
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </div>
      </Dropdown.Toggle>

      <Dropdown.Menu className="notification-menu pt-0">
        <div className="notification-header">
          <h6 className="notification-title">
            Notifications
            {loading && <Spinner animation="border" size="sm" className="ms-2" />}
          </h6>
          <div className="notification-actions">
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={isRefreshing}
              title="Refresh notifications"
            >
              <FaSync className={isRefreshing ? 'spinning' : ''} />
            </button>
            {unreadCount > 0 && (
              <button
                className="btn-mark-all"
                onClick={onMarkAllAsRead}
                title="Mark all as read"
              >
                <FaCheckDouble />
              </button>
            )}
          </div>
        </div>

        <div className="notification-list">
          {loading && notifications.length === 0 ? (
            <div className="notification-loading">
              <Spinner animation="border" size="sm" />
              <span>Loading notifications...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="notification-empty">
              <FaBell size={24} />
              <p>No notifications yet</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                onClick={() => handleNotificationClick(notification)}
                style={{ cursor: 'pointer' }}
              >
                <div className="notification-content">
                  <div className="notification-header-item">
                    {getNotificationIcon(notification.type)}
                    <span className="notification-item-title">{notification.title}</span>
                    <div className="notification-item-actions">
                      {!notification.isRead && <div className="unread-indicator" />}
                      {onDeleteNotification && (
                        <button
                          className="btn-delete-notification"
                          onClick={(e) => handleDeleteClick(e, notification.id)}
                          title="Delete notification"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="notification-message">{notification.message}</p>
                  <span className="notification-time">
                    {formatNotificationTime(notification.createdAt)}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {notifications.length > 0 && (
          <div className="notification-footer">
            <small className="text-muted">
              {unreadCount > 0
                ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
                : 'All notifications read'
              }
            </small>
          </div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default NotificationDropdown;